import type { RouteObject } from 'react-router';

import { lazy, Suspense } from 'react';
import { Outlet, Navigate } from 'react-router';

import { useTranslate } from 'src/locales';
import { MainLayout } from 'src/layouts/main';
import { ShareGuard } from 'src/share/guard/share-guard';
import { getPoopDataLocalizedPath, POOP_DATA_SUPPORTED_LOCALES } from 'src/pages/poop-data/seo';

import { SplashScreen } from 'src/components/loading-screen';

import { PUBLIC_PATH } from '../paths';

// ----------------------------------------------------------------------

function removeSlash(path: string): string {
  return path.startsWith('/') ? path.slice(1) : path;
}

function PoopDataLanguageRedirect() {
  const { currentLang } = useTranslate();

  return <Navigate to={getPoopDataLocalizedPath(currentLang.value)} replace />;
}

// ----------------------------------------------------------------------

const Page404 = lazy(() => import('src/pages/error/404'));
// Terms
const Loc = lazy(() => import('src/pages/terms/loc'));
const Pay = lazy(() => import('src/pages/terms/pay'));
const Privacy = lazy(() => import('src/pages/terms/privacy'));
const Used = lazy(() => import('src/pages/terms/used'));
// landing
const AppInfo = lazy(() => import('src/pages/appInfo'));
const VerifyScreen = lazy(() => import('src/pages/share/verify'));
const DataScreen = lazy(() => import('src/pages/share/data'));
const BoardDetailPage = lazy(() => import('src/pages/board/details'));
const BoardListPage = lazy(() => import('src/pages/board/list'));
const InquiryPage = lazy(() => import('src/pages/inquiry'));
const PoopDataPage = lazy(() => import('src/pages/poop-data'));
const PoopDataPurchasePage = lazy(() => import('src/pages/poop-data/purchase'));
const PoopDataPurchaseCompletePage = lazy(
  () => import('src/pages/poop-data/purchase/complete')
);
const FeedSearchPage = lazy(() => import('src/pages/feed/search'));
const FeedFoodRequestPage = lazy(() => import('src/pages/feed/request'));
const FeedFoodRequestCompletePage = lazy(() => import('src/pages/feed/request-complete'));
const FeedDetailsPage = lazy(() => import('src/pages/feed/details'));
const IdCardCreatePage = lazy(() => import('src/pages/idcard/create'));
const IdCardDetailPage = lazy(() => import('src/pages/idcard/carddetail'));
const PageExpired = lazy(() => import('src/pages/error/expired'));
// ----------------------------------------------------------------------

export const mainRoutes: RouteObject[] = [
  {
    element: (
      <Suspense fallback={<SplashScreen />}>
        <Outlet />
      </Suspense>
    ),
    children: [
      {
        element: (
          <MainLayout>
            <Outlet />
          </MainLayout>
        ),
        children: [
          { path: removeSlash(PUBLIC_PATH.appInfo), element: <AppInfo /> },
          { path: removeSlash(PUBLIC_PATH.board.root), element: <BoardListPage /> },
          { path: removeSlash(PUBLIC_PATH.inquiry), element: <InquiryPage /> },
          {
            path: removeSlash(PUBLIC_PATH.poopData),
            element: <PoopDataLanguageRedirect />,
          },
          { path: removeSlash(PUBLIC_PATH.poopDataPurchase), element: <PoopDataPurchasePage /> },
          {
            path: removeSlash(PUBLIC_PATH.poopDataPurchaseComplete),
            element: <PoopDataPurchaseCompletePage />,
          },
          ...POOP_DATA_SUPPORTED_LOCALES.map((locale) => ({
            path: removeSlash(getPoopDataLocalizedPath(locale)),
            element: <PoopDataPage locale={locale} />,
          })),
          { path: removeSlash(PUBLIC_PATH.board.details(':id')), element: <BoardDetailPage /> },
          { path: removeSlash(PUBLIC_PATH.feed.search), element: <FeedSearchPage /> },
          { path: removeSlash(PUBLIC_PATH.feed.request), element: <FeedFoodRequestPage /> },
          { path: removeSlash(PUBLIC_PATH.feed.details(':uuid')), element: <FeedDetailsPage /> },
          { path: removeSlash(PUBLIC_PATH.idcard.create), element: <IdCardCreatePage /> },
          { path: removeSlash(PUBLIC_PATH.idcard.carddetail), element: <IdCardDetailPage /> },
          {
            path: removeSlash(PUBLIC_PATH.feed.requestComplete),
            element: <FeedFoodRequestCompletePage />,
          },
          {
            path: removeSlash(PUBLIC_PATH.terms.root),
            children: [
              { path: 'privacy', element: <Privacy /> },
              { path: 'used', element: <Used /> },
              { path: 'loc', element: <Loc /> },
              { path: 'pay', element: <Pay /> },
            ],
          },
        ],
      },
      { path: removeSlash(PUBLIC_PATH.share.verifyRoute), element: <VerifyScreen /> },
      {
        path: removeSlash(PUBLIC_PATH.share.dataRoute),
        element: (
          <ShareGuard>
            <DataScreen />
          </ShareGuard>
        ),
      },
      { path: 'error/expired', element: <PageExpired /> },
      { path: 'error/404', element: <Page404 /> },
    ],
  },
];
